import { getResponseStatus, PROTECTED_BRANCH, readConflictDetail, STALE_SYSTEM_HEAD } from "@istari-frontend/shared";

import { branchCommitCommands } from "~/branching/data-source/branch-commit-commands";
import type { BranchChange, BranchCommitted, BranchHead } from "~/branching/model/branch-change";
import { PROTECTED_BRANCH_REASON } from "~/branching/model/protected-branch";
import { PublishingProblem } from "~/libraries/model/publishing-error";

/**
 * Commits to a branch at the head the caller read. If another commit moved the branch since, the
 * server refuses with a stale-head conflict; the change is then rebuilt once against the branch's
 * current head, so a resource created just before the commit is never left untracked. The rebuilt
 * change also gets that head's configuration, for a change that carries one forward. A refusal
 * because the branch is protected rejects as a protected-branch PublishingProblem.
 */
export async function commitAtCurrentHead(
  systemId: string,
  branchId: string,
  expectedHeadSnapshotId: string,
  change: (head: BranchHead) => BranchChange | Promise<BranchChange>,
): Promise<BranchCommitted> {
  try {
    return await commit(
      systemId,
      branchId,
      expectedHeadSnapshotId,
      await change({ snapshotId: expectedHeadSnapshotId }),
    );
  } catch (error) {
    if (!(await isStaleHead(error))) throw error;
    const snapshotId = await branchCommitCommands.headSnapshotId(branchId);
    const configurationId = await branchCommitCommands.configurationId(snapshotId);
    return commit(systemId, branchId, snapshotId, await change({ snapshotId, configurationId }));
  }
}

async function commit(
  systemId: string,
  branchId: string,
  expectedHeadSnapshotId: string,
  change: BranchChange,
): Promise<BranchCommitted> {
  try {
    return await branchCommitCommands.commit(systemId, branchId, expectedHeadSnapshotId, change);
  } catch (error) {
    if ((await conflictCodeOf(error)) !== PROTECTED_BRANCH) throw error;
    throw new PublishingProblem("protected-branch", PROTECTED_BRANCH_REASON, error);
  }
}

async function isStaleHead(error: unknown): Promise<boolean> {
  return (await conflictCodeOf(error)) === STALE_SYSTEM_HEAD;
}

async function conflictCodeOf(error: unknown): Promise<string | undefined> {
  return getResponseStatus(error) === 409 ? (await readConflictDetail(error))?.errorCode : undefined;
}
